import { useState, useRef } from 'react';
import { NodeProps, Node, Handle, Position } from '@xyflow/react';
import { Result1 } from '@mm-shared/types/Result1';
import { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import { GRID_CONFIG } from '../gridConfig';
import { FlowTooltip } from './FlowTooltip';
import {
  getResultCircleLayoutForReactFlow,
  getResultCircleColor,
  getRelativeCirclePosition,
} from '../../utils/resultCircleUtils';

export interface ResultCircleNodeData extends Record<string, unknown> {
  result: Result1;
  objectiveUuid: string;
  rowIndex: number;
  columnIndex: number;
  zoomLevel?: MilestoneMapInterval;
  // Props for circle positioning within cell
  resultIndex: number; // Position within the cell's result array
  totalResults: number; // Total results in the same cell
  siblingResults: Result1[]; // All results in the same cell
  statusCode: string; // Status code for the cell containing this result
  enableFill?: boolean; // Whether to fill circles or make them hollow
  showStatusCodes?: boolean; // Whether to show M/i indicators
  // Callbacks for mode switching
  onEnterDragAndConnectMode?: (resultUuid: string) => void;
}

export type CustomResultCircleNode = Node<ResultCircleNodeData>;

/**
 * ResultCircleNode Component (Visual Layout Version)
 *
 * Optimized for visual layout within cells. Uses complex grid positioning
 * to show multiple circles per cell. No handles, no drag functionality.
 * Click to enter edit mode for interactions.
 */
export function ResultCircleNode(props: NodeProps<CustomResultCircleNode>) {
  const { data } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  const circleRef = useRef<HTMLDivElement>(null);

  // Calculate circle layout based on total results in cell
  const layout = getResultCircleLayoutForReactFlow(data.totalResults);
  const circleColor = getResultCircleColor(data.result, data.statusCode, data.enableFill ?? true);

  // Calculate relative position within the cell
  const relativePosition = getRelativeCirclePosition(
    data.resultIndex,
    data.totalResults,
    GRID_CONFIG.cellWidth,
    GRID_CONFIG.cellHeight,
  );

  const handleClick = () => {
    console.log('ResultCircleNode clicked:', data.result.uuid);
    console.log('onEnterDragAndConnectMode callback:', data.onEnterDragAndConnectMode);

    // Enter drag and connect mode for this specific result
    if (data.onEnterDragAndConnectMode) {
      data.onEnterDragAndConnectMode(data.result.uuid);
      console.log('Called onEnterDragAndConnectMode for:', data.result.uuid);
    } else {
      console.warn('onEnterDragAndConnectMode callback not provided');
    }
  };

  return (
    <div
      className="group relative"
      style={{
        width: `${GRID_CONFIG.cellWidth}px`,
        height: `${GRID_CONFIG.cellHeight}px`,
        pointerEvents: 'none', // Container doesn't intercept events
      }}
    >
      {/* Handles positioned exactly at each circle's edges for proper connection alignment */}
      <Handle
        type="source"
        position={Position.Left}
        style={{
          left: `${relativePosition.x}px`, // Left edge of this specific circle
          top: `${relativePosition.y + layout.radius}px`, // Center of this specific circle
          width: '0px', // No width or height for invisible handles (otherwise they obstruct hover/click for tiny circles with lots of results)
          height: '0px', // No width or height for invisible handles (otherwise they obstruct hover/click for tiny circles with lots of results)
          background: '#6b7280',
          borderRadius: '50%',
          opacity: 0, // Always hidden for visual layout
          zIndex: 10,
          transform: 'translateY(-50%)', // Center handle vertically on the circle
        }}
      />

      <Handle
        type="target"
        position={Position.Right}
        style={{
          left: `${relativePosition.x + layout.radius * 2 - 10}px`, // Right edge of circle, centered on edge (4px = half handle width)
          top: `${relativePosition.y + layout.radius}px`, // Center of this specific circle
          width: '0px', // No width or height for invisible handles (otherwise they obstruct hover/click for tiny circles with lots of results)
          height: '0px', // No width or height for invisible handles (otherwise they obstruct hover/click for tiny circles with lots of results)
          background: '#6b7280',
          borderRadius: '50%',
          opacity: 0, // Always hidden for visual layout
          zIndex: 10,
          transform: 'translateY(-50%)', // Center handle vertically on the circle
        }}
      />

      {/* The actual circle representing the result */}
      <div
        ref={circleRef}
        className="absolute rounded-full border-2 border-white flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={handleClick}
        style={{
          width: `${layout.radius * 2}px`,
          height: `${layout.radius * 2}px`,
          left: `${relativePosition.x}px`,
          top: `${relativePosition.y}px`,
          backgroundColor: circleColor,
          pointerEvents: 'auto', // Circle itself handles events
          zIndex: showTooltip ? GRID_CONFIG.zIndex.nodes + 1 : GRID_CONFIG.zIndex.nodes, // Use centralized z-index, boost on hover
        }}
      >
        {data.showStatusCodes && (
          <div className="text-xs font-semibold text-white">
            {data.result.isKeyResult ? 'M' : 'i'}
          </div>
        )}
      </div>

      {/* Tooltip using FlowTooltip component for proper layering and viewport handling */}
      <FlowTooltip
        show={showTooltip}
        targetRef={circleRef}
        content={
          <div>
            <div className="font-semibold">
              {data.result.isKeyResult ? 'Milestone' : 'Inchstone'}
            </div>
            <div className="max-w-xs">{data.result.text}</div>
            <div className="text-gray-300">
              {data.result.delivered ? 'Delivered' : 'Pending'}
              {data.result.deadline &&
                ` • Due: ${new Date(data.result.deadline).toLocaleDateString()}`}
            </div>
            <div className="text-xs text-gray-400 mt-1">Click to enter drag mode</div>
          </div>
        }
      />
    </div>
  );
}

// Register custom node type
export const circleNodeTypes = {
  resultCircleNode: ResultCircleNode,
};
